/**
 * MomentumCard - ADX & Regime Indicator
 *
 * Displays: ADX value, market regime (TREND/RANGE), DI+/DI-
 *
 * @version 1.0.0
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import IndicatorCard from '../IndicatorCard';

const MomentumCard = memo(({ adxAnalysis, currentState }) => {
    // Get ADX from extended endpoint or currentState fallback
    const h4Adx = adxAnalysis?.h4?.adx || currentState?.volatility?.h4Adx;
    const d1Adx = adxAnalysis?.d1?.adx || currentState?.volatility?.d1Adx;
    const regime = adxAnalysis?.regime || 'UNKNOWN';

    const getRegimeColor = (r) => {
        if (!r) return '#71717A';
        const reg = r.toUpperCase();
        if (reg.includes('STRONG_TREND')) return '#22c55e';
        if (reg.includes('TREND')) return '#4ade80';
        if (reg.includes('RANGE') || reg.includes('WEAK')) return '#fbbf24';
        return '#71717A';
    };

    const getStatus = (adx) => {
        if (!adx) return 'neutral';
        if (adx >= 25) return 'bullish'; // Strong trend
        if (adx >= 20) return 'neutral'; // Weak trend
        return 'bearish'; // Ranging
    };

    const regimeColor = getRegimeColor(regime);

    const summary = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* ADX Value */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#FAFAFA',
                        fontFamily: 'monospace'
                    }}
                >
                    {h4Adx?.toFixed(1) || '--'}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '10px',
                        color: '#71717A'
                    }}
                >
                    ADX
                </Typography>
            </Box>

            {/* Regime Badge */}
            <Box
                sx={{
                    backgroundColor: `${regimeColor}20`,
                    color: regimeColor,
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '4px',
                    textTransform: 'uppercase'
                }}
            >
                {regime.replace('_', ' ')}
            </Box>
        </Box>
    );

    return (
        <IndicatorCard
            title="MOMENTUM"
            icon="⚡"
            summary={summary}
            status={getStatus(h4Adx)}
            defaultExpanded={false}
        >
            {/* Expanded: Multi-TF ADX breakdown */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* H4 Row */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '4px'
                    }}
                >
                    <Typography sx={{ fontSize: '10px', color: '#A1A1AA', fontFamily: 'monospace' }}>
                        H4
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography sx={{ fontSize: '11px', color: '#FAFAFA', fontWeight: 600 }}>
                            ADX: {h4Adx?.toFixed(1) || '--'}
                        </Typography>
                        {adxAnalysis?.h4?.plusDI && (
                            <Typography sx={{ fontSize: '10px', color: '#22c55e' }}>
                                +DI: {adxAnalysis.h4.plusDI.toFixed(1)}
                            </Typography>
                        )}
                        {adxAnalysis?.h4?.minusDI && (
                            <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>
                                -DI: {adxAnalysis.h4.minusDI.toFixed(1)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* D1 Row */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '4px'
                    }}
                >
                    <Typography sx={{ fontSize: '10px', color: '#A1A1AA', fontFamily: 'monospace' }}>
                        D1
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography sx={{ fontSize: '11px', color: '#FAFAFA', fontWeight: 600 }}>
                            ADX: {d1Adx?.toFixed(1) || '--'}
                        </Typography>
                        {adxAnalysis?.d1?.plusDI && (
                            <Typography sx={{ fontSize: '10px', color: '#22c55e' }}>
                                +DI: {adxAnalysis.d1.plusDI.toFixed(1)}
                            </Typography>
                        )}
                        {adxAnalysis?.d1?.minusDI && (
                            <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>
                                -DI: {adxAnalysis.d1.minusDI.toFixed(1)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* ADX Legend */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.5 }}>
                    <Typography sx={{ fontSize: '9px', color: '#52525B' }}>
                        &lt;20: Ranging
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: '#52525B' }}>
                        20-25: Weak Trend
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: '#52525B' }}>
                        &gt;25: Strong Trend
                    </Typography>
                </Box>
            </Box>
        </IndicatorCard>
    );
});

MomentumCard.displayName = 'MomentumCard';

export default MomentumCard;
